import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Volume2, VolumeX, HelpCircle, Copy, Check, ArrowRight, Sparkles } from 'lucide-react';
import './CyberForestHunt.css';

// High-quality predator assets
const PREDATOR_ASSETS = {
  'phish-panther': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/705ca878312301ce9f5fceaaebe7039369f5a9142a46d5cc3f06287b1802f7b9.png',
  'scam-siren': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/b6978e13dec104a6b4172d13440382aa2248df63605ebf3e8f7addf9bebe978a.png',
  'ransom-rhino': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/96f2d62f87b88ce6c2b466b9a02ff47a6996b57472b6b0cd696d38bc49e0aa3f.png',
  'malware-mantis': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/6df0a501539bd8d4332deac9643911606f3e1a1a2bf1873d6ebc3640a6bf666b.png',
  'spyware-serpent': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/4a0b2802567ab39efc245eb6dcac4d23db0f67169e74894af4cd54b6fafc7966.png',
  'credential-crow': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/eb6c03c0c8833dc0d2f6e98047caa46526fce2b312b036c4b09d0349c85afda2.png',
  'sim-swap-spider': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/8f38e8559ee55e89b74daec41310603ba0d983b091ce0c232a2b8a56f3629267.png',
  'tracker-toad': 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/25dd51cfde0b2ea7ca2d3396677f36b882a011bd166350073c54f3bcd1401612.png',
};

const FOREST_BG = 'https://static.prod-images.emergentagent.com/jobs/d149ceb2-db51-4695-8892-3fa07f4a879b/images/789e5facce6ccd93a8207ff7f8401dbbe681fadc6afc2f7e2269db77aed1c192.png';

// Cyber predator definitions with positions optimized for the forest scene
const PREDATORS = [
  { id: 'phish-panther', name: 'Phish Panther', threat: 'Phishing links & fake logins', position: { x: 12, y: 55 }, size: 90 },
  { id: 'scam-siren', name: 'Scam Siren', threat: 'Social engineering / impersonation', position: { x: 85, y: 45 }, size: 85 },
  { id: 'ransom-rhino', name: 'Ransom Rhino', threat: 'Ransomware / extortion', position: { x: 50, y: 65 }, size: 95 },
  { id: 'malware-mantis', name: 'Malware Mantis', threat: 'Malicious downloads & trojans', position: { x: 75, y: 25 }, size: 80 },
  { id: 'spyware-serpent', name: 'Spyware Serpent', threat: 'Stalkerware / hidden monitoring', position: { x: 25, y: 75 }, size: 85 },
  { id: 'credential-crow', name: 'Credential Crow', threat: 'Password reuse / credential stuffing', position: { x: 65, y: 15 }, size: 75 },
  { id: 'sim-swap-spider', name: 'SIM Swap Spider', threat: 'Account takeover via SIM swapping', position: { x: 8, y: 28 }, size: 70 },
  { id: 'tracker-toad', name: 'Tracker Toad', threat: 'Tracking / data brokers / ad profiling', position: { x: 40, y: 80 }, size: 70 },
];

const VOUCHER_CODE = 'FOREST8';
const VOUCHER_STORAGE_KEY = 'huntDiscount';

// Event tracking helper
const trackEvent = (eventName, data = {}) => {
  console.log(`[CyberForestHunt] ${eventName}`, data);
  if (window.gtag) {
    window.gtag('event', eventName, { event_category: 'cyber_forest_hunt', ...data });
  }
  if (window.fbq) {
    window.fbq('trackCustom', eventName, data);
  }
};

// Store voucher in localStorage (called on completion)
const storeVoucher = () => {
  try {
    const voucherData = {
      code: VOUCHER_CODE,
      percent: 8,
      expires: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      claimed: false,
      createdAt: Date.now()
    };
    localStorage.setItem(VOUCHER_STORAGE_KEY, JSON.stringify(voucherData));
    console.log('[CyberForestHunt] Voucher stored:', voucherData);
    return true;
  } catch (e) {
    console.error('[CyberForestHunt] Failed to store voucher:', e);
    return false;
  }
};

const CyberForestHuntModal = ({ 
  isOpen, 
  onClose, 
  onComplete,
  courseSlug = 'cyber-privacy-security-basics'
}) => {
  const [foundPredators, setFoundPredators] = useState([]);
  const [hintsRemaining, setHintsRemaining] = useState(3);
  const [hintTarget, setHintTarget] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [recentlyFound, setRecentlyFound] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  const modalRef = useRef(null);
  
  // Reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Pre-generate particle positions
  const particlePositions = useMemo(() => 
    [...Array(20)].map(() => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 8,
      size: 2 + Math.random() * 4
    })), []
  );

  // Preload images
  useEffect(() => {
    if (!isOpen) return;
    
    const imagesToLoad = [FOREST_BG, ...Object.values(PREDATOR_ASSETS)];
    let loadedCount = 0;
    
    imagesToLoad.forEach(src => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === imagesToLoad.length) {
          setImagesLoaded(true);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === imagesToLoad.length) {
          setImagesLoaded(true);
        }
      };
      img.src = src;
    });
  }, [isOpen]);

  // Track popup shown
  useEffect(() => {
    if (isOpen) {
      trackEvent('popup_shown');
      trackEvent('hunt_started');
    }
  }, [isOpen]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
        if (e.key === 'Escape') {
          handleClose();
        }
      };

      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();

      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen]);

  // Play sound effect
  const playSound = useCallback((type) => {
    if (!soundEnabled) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'ping') {
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1047, audioContext.currentTime + 0.1);
        oscillator.frequency.exponentialRampToValueAtTime(784, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.4);
      } else if (type === 'whoosh') {
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.3);
        oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.6);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.8);
      }
    } catch (e) {
      // Audio not supported
    }
  }, [soundEnabled]);

  // Handle predator click
  const handlePredatorClick = (predatorId) => {
    if (foundPredators.includes(predatorId)) return;
    
    setFoundPredators(prev => [...prev, predatorId]);
    setRecentlyFound(predatorId);
    playSound('ping');
    trackEvent('predator_found', { predator_id: predatorId });
    
    // Clear recently found after animation
    setTimeout(() => setRecentlyFound(null), 1500);
    
    // Check for completion (8 predators total, so check if this is the 8th)
    if (foundPredators.length === 7) {
      setTimeout(() => {
        setIsComplete(true);
        playSound('whoosh');
        trackEvent('hunt_completed');
        
        // Store voucher immediately on completion
        storeVoucher();
        
        // Notify parent that hunt is complete
        if (onComplete) {
          onComplete(VOUCHER_CODE);
        }
      }, 800);
    }
  };

  // Handle hint
  const handleHint = () => {
    if (hintsRemaining <= 0) return;
    
    const unfoundPredators = PREDATORS.filter(p => !foundPredators.includes(p.id));
    if (unfoundPredators.length === 0) return;
    
    const randomPredator = unfoundPredators[Math.floor(Math.random() * unfoundPredators.length)];
    setHintTarget(randomPredator.id);
    setHintsRemaining(prev => prev - 1);
    trackEvent('hints_used', { hints_remaining: hintsRemaining - 1 });
    
    setTimeout(() => setHintTarget(null), 2500);
  };

  // Handle close
  const handleClose = useCallback(() => {
    trackEvent('popup_closed', { predators_found: foundPredators.length });
    onClose();
  }, [foundPredators.length, onClose]);

  // Handle skip
  const handleSkip = useCallback(() => {
    trackEvent('popup_closed', { predators_found: foundPredators.length, skipped: true });
    onClose();
  }, [foundPredators.length, onClose]);

  // Handle copy code
  const handleCopyCode = () => {
    navigator.clipboard.writeText(VOUCHER_CODE).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  // Handle checkout - store voucher and redirect to course page
  const handleCheckout = () => {
    trackEvent('voucher_clicked');
    trackEvent('checkout_started_from_hunt');
    
    // Store voucher with full data structure
    const voucherData = {
      code: VOUCHER_CODE,
      percent: 8,
      expires: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      claimed: false,
      createdAt: Date.now()
    };
    localStorage.setItem(VOUCHER_STORAGE_KEY, JSON.stringify(voucherData));
    console.log('[CyberForestHunt] Voucher stored for checkout:', voucherData);
    
    // Redirect with discount code in URL (double protection)
    window.location.href = `/courses/${courseSlug}?discount=${VOUCHER_CODE}`;
    
    if (onComplete) onComplete(VOUCHER_CODE);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`cfh-overlay ${prefersReducedMotion ? 'reduced-motion' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cfh-title"
    >
      <div className="cfh-modal cfh-premium" ref={modalRef}>
        {/* Header */}
        <div className="cfh-header">
          <div className="cfh-header-left">
            <div className="cfh-title-row">
              <Sparkles size={20} className="cfh-title-icon" />
              <h2 id="cfh-title" className="cfh-title">Cyber Forest Hunt</h2>
            </div>
            <div className="cfh-progress-bar">
              <div 
                className="cfh-progress-fill" 
                style={{ width: `${(foundPredators.length / 8) * 100}%` }}
              />
            </div>
            <span className="cfh-progress-text">{foundPredators.length}/8 predators found</span>
          </div>
          <div className="cfh-header-right">
            <button 
              className="cfh-sound-toggle"
              onClick={() => setSoundEnabled(!soundEnabled)}
              aria-label={soundEnabled ? 'Mute sound' : 'Enable sound'}
            >
              {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <button 
              className="cfh-close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Subtitle */}
        {!isComplete && (
          <div className="cfh-subtitle-area">
            <p className="cfh-subtitle">
              Find the 8 hidden cyber predators lurking in the forest.
              <span className="cfh-subtitle-reward">Unlock <strong>8% off</strong> when you secure the forest!</span>
            </p>
          </div>
        )}

        {/* Main Content */}
        {!isComplete ? (
          <>
            {/* Loading state */}
            {!imagesLoaded && (
              <div className="cfh-loading">
                <div className="cfh-loading-spinner" />
                <p>Loading the Cyber Forest...</p>
              </div>
            )}
            
            {/* Forest Scene */}
            <div 
              className={`cfh-forest-container ${imagesLoaded ? 'loaded' : ''}`}
              role="application"
              aria-label="Cyber forest scene - click to find hidden predators"
            >
              {/* Forest Background */}
              <div 
                className="cfh-forest-bg"
                style={{ backgroundImage: `url(${FOREST_BG})` }}
              />
              
              {/* Ambient glow overlay */}
              <div className="cfh-forest-glow" />
              
              {/* Ambient particles */}
              <div className="cfh-forest-particles">
                {particlePositions.map((pos, i) => (
                  <div 
                    key={i} 
                    className="cfh-particle" 
                    style={{ 
                      left: `${pos.left}%`, 
                      top: `${pos.top}%`,
                      animationDelay: `${pos.delay}s`,
                      width: `${pos.size}px`,
                      height: `${pos.size}px`
                    }} 
                  />
                ))}
              </div>

              {/* Predator Hotspots with actual images */}
              {PREDATORS.map((predator) => {
                const isFound = foundPredators.includes(predator.id);
                const isHinted = hintTarget === predator.id;
                const isRecent = recentlyFound === predator.id;
                
                return (
                  <button
                    key={predator.id}
                    className={`cfh-predator ${isFound ? 'found' : ''} ${isHinted ? 'hint-active' : ''} ${isRecent ? 'just-found' : ''}`}
                    style={{ 
                      left: `${predator.position.x}%`, 
                      top: `${predator.position.y}%`,
                      '--predator-size': `${predator.size}px`
                    }}
                    onClick={() => handlePredatorClick(predator.id)}
                    aria-label={isFound ? `${predator.name} - found` : 'Hidden predator'}
                    disabled={isFound}
                  >
                    <div className="cfh-predator-glow" />
                    <img 
                      src={PREDATOR_ASSETS[predator.id]} 
                      alt={isFound ? predator.name : ''} 
                      className="cfh-predator-img"
                      loading="lazy"
                    />
                    {isFound && (
                      <div className="cfh-predator-label">
                        <span className="cfh-predator-name">{predator.name}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Collection Tray */}
            <div className="cfh-collection">
              <div className="cfh-collection-label">Your Collection</div>
              <div className="cfh-collection-grid">
                {PREDATORS.map((predator) => {
                  const isFound = foundPredators.includes(predator.id);
                  return (
                    <div 
                      key={predator.id}
                      className={`cfh-collection-slot ${isFound ? 'collected' : ''}`}
                      title={isFound ? `${predator.name}: ${predator.threat}` : 'Not found yet'}
                    >
                      {isFound ? (
                        <img 
                          src={PREDATOR_ASSETS[predator.id]} 
                          alt={predator.name}
                          className="cfh-collection-img"
                        />
                      ) : (
                        <span className="cfh-slot-placeholder">?</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* Completion State */
          <div className="cfh-complete">
            <div className="cfh-complete-celebration">
              <div className="cfh-complete-glow-ring" />
              <div className="cfh-complete-glow-ring delay-1" />
              <div className="cfh-complete-glow-ring delay-2" />
            </div>
            
            <div className="cfh-complete-content">
              <Sparkles size={40} className="cfh-complete-icon" />
              <h3 className="cfh-complete-title">Forest Secured!</h3>
              <p className="cfh-complete-text">You&apos;ve spotted the threats most people miss.</p>
              
              <div className="cfh-voucher">
                <div className="cfh-voucher-badge">UNLOCKED</div>
                <div className="cfh-voucher-value">8% OFF</div>
                <div className="cfh-voucher-code-row">
                  <span className="cfh-voucher-code">{VOUCHER_CODE}</span>
                  <button 
                    className="cfh-copy-btn"
                    onClick={handleCopyCode}
                    aria-label="Copy discount code"
                  >
                    {codeCopied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <button className="cfh-checkout-btn" onClick={handleCheckout}>
                Apply Discount & Continue
                <ArrowRight size={18} />
              </button>
              
              <p className="cfh-complete-timer">Valid for 24 hours</p>
            </div>

            {/* Show collected predators */}
            <div className="cfh-complete-collection">
              <p className="cfh-complete-collection-label">Threats Identified:</p>
              <div className="cfh-complete-collection-grid">
                {PREDATORS.map((predator) => (
                  <div key={predator.id} className="cfh-complete-predator" title={predator.threat}>
                    <img src={PREDATOR_ASSETS[predator.id]} alt={predator.name} />
                    <span>{predator.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="cfh-footer">
          {!isComplete ? (
            <>
              <button className="cfh-skip-btn" onClick={handleSkip}>
                Skip
              </button>
              <button 
                className="cfh-hint-btn"
                onClick={handleHint}
                disabled={hintsRemaining <= 0}
              >
                <HelpCircle size={16} />
                Hint ({hintsRemaining})
              </button>
              <button 
                className="cfh-how-btn"
                onClick={() => setShowHowItWorks(!showHowItWorks)}
              >
                How it works
              </button>
              <span className="cfh-footer-reward">
                <Sparkles size={14} />
                Unlock 8% off
              </span>
            </>
          ) : (
            <span className="cfh-footer-complete">Thank you for playing! 🎉</span>
          )}
        </div>

        {/* How It Works Tooltip */}
        {showHowItWorks && (
          <div className="cfh-how-tooltip">
            <h4>How to Play</h4>
            <p>1. Look carefully at the cyber forest</p>
            <p>2. Click on hidden predators when you spot them</p>
            <p>3. Find all 8 to unlock your discount!</p>
            <p className="cfh-how-tip">💡 Use hints if you get stuck</p>
            <button onClick={() => setShowHowItWorks(false)}>Got it!</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CyberForestHuntModal;
